import { useState, useEffect, useMemo } from 'react';
import { captureError } from "@/lib/logger";
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
    Sheet,
    SheetContent,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';

import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Loader2, Wrench, X, Check, Shield, FileCheck, RefreshCw, Sparkles, Settings, HardHat, ChevronRight, ChevronLeft, Calendar, ClipboardCheck, Wallet, Car, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { DatePicker } from '@/components/ui/date-picker';
import TransactionSelector from '@/components/ui/TransactionSelector';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useDebounce } from '@/hooks/useDebounce';

const MAINTENANCE_TYPES = [
    { value: 'servicing', label: 'Servicing', icon: Settings },
    { value: 'insurance', label: 'Insurance', icon: Shield },
    { value: 'warranty', label: 'Warranty', icon: FileCheck },
    { value: 'conversion', label: 'Conversion', icon: RefreshCw },
    { value: 'refinement', label: 'Refinement', icon: Sparkles },
    { value: 'refurbishment', label: 'Refurbishment', icon: HardHat },
] as const;

const BASE_STEPS = [
    { key: 'details', label: 'Details', icon: Wrench, description: 'Type & Description' },
    { key: 'dates', label: 'Dates', icon: Calendar, description: 'Start & End' },
    { key: 'transaction', label: 'Transaction', icon: Wallet, description: 'Link Payment' },
    { key: 'review', label: 'Review', icon: ClipboardCheck, description: 'Confirm Details' },
];

const VEHICLE_STEP = { key: 'vehicle', label: 'Vehicle', icon: Car, description: 'Select Vehicle' };

const variants = {
    enter: (direction: number) => ({ x: direction > 0 ? 50 : -50, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (direction: number) => ({ x: direction > 0 ? -50 : 50, opacity: 0 }),
};

interface AddMaintenanceSheetProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    vehicleId?: number;
    contractId?: number;
    maintenanceId?: number | null;
    onSuccess?: () => void;
}

export default function AddMaintenanceSheet({
    open,
    onOpenChange,
    vehicleId,
    contractId,
    maintenanceId,
    onSuccess,
}: AddMaintenanceSheetProps) {
    const queryClient = useQueryClient();
    const isEditMode = !!maintenanceId;
    // Show vehicle step when creating without a vehicle context, or when editing (allows reassignment)
    const showVehicleStep = !vehicleId || isEditMode;
    const [step, setStep] = useState(0);
    const [direction, setDirection] = useState(0);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

    // Vehicle selection state — initialized from prop when editing
    const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(vehicleId ?? null);
    const [resolvedContractId, setResolvedContractId] = useState<number | null>(null);
    const [vehicleSearch, setVehicleSearch] = useState('');
    const [isDirty, setIsDirty] = useState(false);

    const effectiveVehicleId = showVehicleStep ? selectedVehicleId : (vehicleId ?? selectedVehicleId);
    const effectiveContractId = contractId ?? resolvedContractId;

    const STEPS = useMemo(() =>
        showVehicleStep ? [VEHICLE_STEP, ...BASE_STEPS] : BASE_STEPS
    , [showVehicleStep]);

    const [state, setState] = useState({
        type: 'servicing' as string,
        description: '',
        startedAt: format(new Date(), 'yyyy-MM-dd'),
        endedAt: '',
        transactionId: null as number | null,
    });

    const updateState = (updates: Partial<typeof state>) => {
        setState(prev => ({ ...prev, ...updates }));
        setIsDirty(true);
    };

    // Fetch existing maintenance for edit mode
    const { data: existingData } = useQuery({
        queryKey: ['maintenance-edit', maintenanceId],
        queryFn: async () => {
            if (!maintenanceId) return null;
            const { data, error } = await supabase
                .from('vehicle_maintenances')
                .select(`id, type, description, started_at, ended_at, transaction_id`)
                .eq('id', maintenanceId)
                .single();
            if (error) throw error;
            return data;
        },
        enabled: open && isEditMode,
    });

    // Populate form when editing
    useEffect(() => {
        if (existingData && isEditMode) {
            setState({
                type: existingData.type || 'servicing',
                description: existingData.description || '',
                startedAt: existingData.started_at ? format(new Date(existingData.started_at), 'yyyy-MM-dd') : '',
                endedAt: existingData.ended_at ? format(new Date(existingData.ended_at), 'yyyy-MM-dd') : '',
                transactionId: existingData.transaction_id || null,
            });
        }
    }, [existingData, isEditMode]);

    // Fetch vehicles for the vehicle selection step
    const debouncedVehicleSearch = useDebounce(vehicleSearch, 300);
    const { data: vehicleOptions, isLoading: isLoadingVehicles } = useQuery({
        queryKey: ['vehicles-for-maintenance', debouncedVehicleSearch],
        queryFn: async () => {
            const search = debouncedVehicleSearch?.trim();
            if (search) {
                // Search by make/model/vin AND by registration number
                // Use !inner join to find vehicle IDs that match registration numbers
                const [vehicleRes, regRes] = await Promise.all([
                    supabase
                        .from('vehicles')
                        .select('id, vin, make, model, vehicle_registrations(number)')
                        .or(`make.ilike.%${search}%,model.ilike.%${search}%,vin.ilike.%${search}%`)
                        .limit(50),
                    supabase
                        .from('vehicles')
                        .select('id, vin, make, model, vehicle_registrations!inner(number)')
                        .ilike('vehicle_registrations.number', `%${search}%`)
                        .limit(100),
                ]);
                if (vehicleRes.error) throw vehicleRes.error;
                if (regRes.error) throw regRes.error;

                // Merge: reg matches first (most relevant), then make/model/vin matches
                const seen = new Set<number>();
                const merged: any[] = [];
                for (const v of regRes.data || []) {
                    if (!seen.has(v.id)) {
                        seen.add(v.id);
                        merged.push(v);
                    }
                }
                for (const v of vehicleRes.data || []) {
                    if (!seen.has(v.id)) {
                        seen.add(v.id);
                        merged.push(v);
                    }
                }
                return merged;
            }
            // No search — show all vehicles, registered ones first
            const { data, error } = await supabase
                .from('vehicles')
                .select('id, vin, make, model, vehicle_registrations(number)')
                .order('created_at', { ascending: false })
                .limit(200);
            if (error) throw error;
            // Sort: vehicles with registrations first
            return (data || []).sort((a: any, b: any) => {
                const aReg = Array.isArray(a.vehicle_registrations) ? a.vehicle_registrations.length : (a.vehicle_registrations ? 1 : 0);
                const bReg = Array.isArray(b.vehicle_registrations) ? b.vehicle_registrations.length : (b.vehicle_registrations ? 1 : 0);
                return bReg - aReg;
            });
        },
        enabled: open && showVehicleStep,
    });

    const handleSelectVehicle = async (vId: number) => {
        setSelectedVehicleId(vId);
        if (vId !== vehicleId) setIsDirty(true);
        // Resolve the latest active contract for this vehicle
        const { data } = await supabase
            .from('contracts')
            .select('id, contract_updates(status, created_at)')
            .eq('vehicle_id', vId)
            .order('created_at', { ascending: false })
            .limit(10);
        const activeContract = data?.find((c: any) => {
            const updates = c.contract_updates || [];
            const latest = [...updates].sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
            return latest?.status === 'IN_PROGRESS';
        });
        setResolvedContractId(activeContract?.id ?? null);
    };

    const resetAndClose = () => {
        setStep(0);
        setState({
            type: 'servicing',
            description: '',
            startedAt: format(new Date(), 'yyyy-MM-dd'),
            endedAt: '',
            transactionId: null,
        });
        setSelectedVehicleId(null);
        setResolvedContractId(null);
        setVehicleSearch('');
        setShowCloseConfirm(false);
        onOpenChange(false);
    };

    useEffect(() => {
        if (!open) {
            setStep(0);
            setState({
                type: 'servicing',
                description: '',
                startedAt: format(new Date(), 'yyyy-MM-dd'),
                endedAt: '',
                transactionId: null,
            });
            setSelectedVehicleId(null);
            setResolvedContractId(null);
            setVehicleSearch('');
            setIsDirty(false);
        } else if (vehicleId) {
            setSelectedVehicleId(vehicleId);
        }
    }, [open, vehicleId]);

    const canGoNext = () => {
        const currentKey = STEPS[step]?.key;
        switch (currentKey) {
            case 'vehicle': return effectiveVehicleId !== null;
            case 'details': return state.type !== '' && state.description.trim() !== '';
            case 'dates': return state.startedAt !== '';
            case 'transaction': return state.transactionId !== null;
            case 'review': return true;
            default: return false;
        }
    };

    const handleNext = () => {
        if (step < STEPS.length - 1) {
            setDirection(1);
            setStep(step + 1);
        }
    };

    const handleBack = () => {
        if (step > 0) {
            setDirection(-1);
            setStep(step - 1);
        }
    };

    const handleClose = () => {
        if (isDirty) {
            setShowCloseConfirm(true);
        } else {
            onOpenChange(false);
        }
    };

    const mutation = useMutation({
        mutationFn: async () => {
            if (!effectiveVehicleId) throw new Error('No vehicle selected');
            const payload = {
                vehicle_id: effectiveVehicleId,
                contract_id: effectiveContractId || null,
                type: state.type,
                description: state.description,
                started_at: state.startedAt,
                ended_at: state.endedAt || null,
                transaction_id: state.transactionId,
            };

            if (isEditMode && maintenanceId) {
                const { error } = await supabase
                    .from('vehicle_maintenances')
                    .update(payload)
                    .eq('id', maintenanceId);
                if (error) throw error;
            } else {
                const { error } = await supabase
                    .from('vehicle_maintenances')
                    .insert(payload);
                if (error) throw error;
            }
        },
        onSuccess: () => {
            toast.success(isEditMode ? 'Maintenance updated' : 'Maintenance added');
            queryClient.invalidateQueries({ queryKey: ['contract-insights'] });
            queryClient.invalidateQueries({ queryKey: ['unassigned-transactions'] });
            queryClient.invalidateQueries({ queryKey: ['maintenances'] });
            queryClient.invalidateQueries({ queryKey: ['maintenances-count'] });
            queryClient.invalidateQueries({ queryKey: ['maintenances-search-data'] });
            resetAndClose();
            onSuccess?.();
        },
        onError: (error: Error) => {
            captureError(error);
            toast.error(error.message || 'Failed to save maintenance');
        },
    });

    const deleteMutation = useMutation({
        mutationFn: async () => {
            if (!maintenanceId) throw new Error('No maintenance to delete');
            const { error } = await supabase
                .from('vehicle_maintenances')
                .delete()
                .eq('id', maintenanceId);
            if (error) throw error;
        },
        onSuccess: () => {
            toast.success('Maintenance deleted');
            queryClient.invalidateQueries({ queryKey: ['contract-insights'] });
            queryClient.invalidateQueries({ queryKey: ['unassigned-transactions'] });
            queryClient.invalidateQueries({ queryKey: ['maintenances'] });
            queryClient.invalidateQueries({ queryKey: ['maintenances-count'] });
            queryClient.invalidateQueries({ queryKey: ['maintenances-search-data'] });
            resetAndClose();
            onSuccess?.();
        },
        onError: (error: Error) => {
            captureError(error);
            toast.error(error.message || 'Failed to delete maintenance');
        },
    });

    const selectedTypeInfo = MAINTENANCE_TYPES.find(t => t.value === state.type) || MAINTENANCE_TYPES[0];
    const TypeIcon = selectedTypeInfo.icon;

    const selectedVehicleInfo = showVehicleStep && selectedVehicleId
        ? vehicleOptions?.find((v: any) => v.id === selectedVehicleId)
        : null;

    const renderStep = () => {
        const currentKey = STEPS[step]?.key;
        switch (currentKey) {
            case 'vehicle':
                return (
                    <div className="space-y-4">
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                                placeholder="Search by reg number, make, or model..."
                                value={vehicleSearch}
                                onChange={(e) => setVehicleSearch(e.target.value)}
                                className="pl-9"
                                autoFocus
                            />
                        </div>
                        {selectedVehicleInfo && (() => {
                            const reg = Array.isArray(selectedVehicleInfo.vehicle_registrations)
                                ? selectedVehicleInfo.vehicle_registrations[0]?.number
                                : selectedVehicleInfo.vehicle_registrations?.number;
                            return (
                                <div className="flex items-center gap-3 p-3 rounded-lg bg-primary/5 border border-primary/20">
                                    <div className="flex items-center justify-center h-8 w-8 rounded-full bg-primary/10">
                                        <Check className="h-4 w-4 text-primary" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{reg || selectedVehicleInfo.vin || 'Unregistered'}</p>
                                        <p className="text-xs text-muted-foreground">{selectedVehicleInfo.make} {selectedVehicleInfo.model}</p>
                                    </div>
                                </div>
                            );
                        })()}
                        <ScrollArea className="h-[calc(100vh-22rem)]">
                            <div className="space-y-2 pr-3">
                                {isLoadingVehicles ? (
                                    <div className="flex flex-col gap-2">
                                        {[1, 2, 3, 4].map(i => (
                                            <div key={i} className="h-16 rounded-lg bg-muted/40 animate-pulse border border-border/40" />
                                        ))}
                                    </div>
                                ) : !vehicleOptions?.length ? (
                                    <div className="flex flex-col items-center justify-center py-12 text-center border-2 border-dashed rounded-lg bg-muted/10">
                                        <Car className="h-8 w-8 text-muted-foreground mb-2" />
                                        <p className="text-sm font-medium">No vehicles found</p>
                                        <p className="text-xs text-muted-foreground mt-1">Try a different search term</p>
                                    </div>
                                ) : (
                                    vehicleOptions.map((v: any) => {
                                        const reg = Array.isArray(v.vehicle_registrations) ? v.vehicle_registrations[0]?.number : v.vehicle_registrations?.number;
                                        const isSelected = v.id === selectedVehicleId;
                                        return (
                                            <button
                                                key={v.id}
                                                type="button"
                                                className={cn(
                                                    'w-full text-left cursor-pointer rounded-lg border p-3 transition-all hover:border-primary hover:bg-primary/5',
                                                    isSelected ? 'border-primary bg-primary/5 ring-1 ring-primary' : 'border-border/60 bg-card',
                                                )}
                                                onClick={() => handleSelectVehicle(v.id)}
                                            >
                                                <div className="flex justify-between items-center">
                                                    <div>
                                                        <p className="font-medium">{reg || v.vin || 'Unregistered'}</p>
                                                        <p className="text-xs text-muted-foreground">{v.make} {v.model}</p>
                                                    </div>
                                                    {isSelected && <Check className="h-4 w-4 text-primary" />}
                                                </div>
                                            </button>
                                        );
                                    })
                                )}
                            </div>
                        </ScrollArea>
                    </div>
                );
            case 'details':
                return (
                    <div className="space-y-6">
                        <div>
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Maintenance Type *</Label>
                            <Select value={state.type} onValueChange={(v) => updateState({ type: v })}>
                                <SelectTrigger className="h-11 mt-2">
                                    <SelectValue placeholder="Select type..." />
                                </SelectTrigger>
                                <SelectContent>
                                    {MAINTENANCE_TYPES.map((t) => {
                                        const Icon = t.icon;
                                        return (
                                            <SelectItem key={t.value} value={t.value}>
                                                <div className="flex items-center gap-2">
                                                    <Icon className="h-4 w-4" />
                                                    {t.label}
                                                </div>
                                            </SelectItem>
                                        );
                                    })}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide" htmlFor="description">Description *</Label>
                            <Textarea
                                id="description"
                                value={state.description}
                                onChange={(e) => updateState({ description: e.target.value })}
                                placeholder="Describe the maintenance work..."
                                rows={4}
                                className="mt-2"
                            />
                        </div>
                    </div>
                );
            case 'dates':
                return (
                    <div className="space-y-6">
                        <div>
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide" htmlFor="startedAt">Start Date *</Label>
                            <DatePicker
                                date={state.startedAt ? new Date(state.startedAt) : undefined}
                                setDate={(date) => updateState({ startedAt: date ? format(date, 'yyyy-MM-dd') : '' })}
                                className="h-11 mt-2"
                            />
                            <p className="text-xs text-muted-foreground mt-1">When the maintenance work started</p>
                        </div>
                        <div>
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide" htmlFor="endedAt">End Date</Label>
                            <DatePicker
                                date={state.endedAt ? new Date(state.endedAt) : undefined}
                                setDate={(date) => updateState({ endedAt: date ? format(date, 'yyyy-MM-dd') : '' })}
                                className="h-11 mt-2"
                            />
                            <p className="text-xs text-muted-foreground mt-1">Leave empty if still ongoing</p>
                        </div>
                    </div>
                );
            case 'transaction':
                return (
                    <div className="space-y-6">
                        <div>
                            <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Transaction *</Label>
                            <p className="text-xs text-muted-foreground mb-3">Link the payment transaction for this maintenance.</p>
                            <TransactionSelector
                                value={state.transactionId}
                                onChange={(id) => updateState({ transactionId: id })}
                                defaultType="DEBIT"
                                placeholder="Select transaction..."
                            />
                        </div>
                    </div>
                );
            case 'review':
                return (
                    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                        <div className="bg-card border rounded-xl shadow-sm overflow-hidden relative">
                            <div className="h-2 bg-gradient-to-r from-primary/50 to-primary w-full" />
                            <div className="p-6 space-y-4">
                                {showVehicleStep && selectedVehicleInfo && (
                                    <div className="flex justify-between items-center py-2 border-b">
                                        <span className="text-muted-foreground">Vehicle</span>
                                        <span className="font-medium">
                                            {(() => {
                                                const reg = Array.isArray(selectedVehicleInfo.vehicle_registrations) ? selectedVehicleInfo.vehicle_registrations[0]?.number : (selectedVehicleInfo.vehicle_registrations as any)?.number;
                                                return reg || `${selectedVehicleInfo.make} ${selectedVehicleInfo.model}`;
                                            })()}
                                        </span>
                                    </div>
                                )}
                                <div className="flex justify-between items-center py-2 border-b">
                                    <span className="text-muted-foreground">Type</span>
                                    <span className="font-medium capitalize flex items-center gap-2">
                                        <TypeIcon className="h-4 w-4" />
                                        {state.type}
                                    </span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b">
                                    <span className="text-muted-foreground">Description</span>
                                    <span className="font-medium text-right max-w-[200px] truncate">{state.description}</span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b">
                                    <span className="text-muted-foreground">Start Date</span>
                                    <span className="font-medium">{state.startedAt ? format(new Date(state.startedAt), 'MMM d, yyyy') : '-'}</span>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b">
                                    <span className="text-muted-foreground">End Date</span>
                                    <span className="font-medium">{state.endedAt ? format(new Date(state.endedAt), 'MMM d, yyyy') : 'Ongoing'}</span>
                                </div>
                                <div className="flex justify-between items-center py-2">
                                    <span className="text-muted-foreground">Transaction</span>
                                    <span className="font-medium text-emerald-600">Linked</span>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <>
            <Sheet open={open} onOpenChange={handleClose}>
                <SheetContent
                    side="right"
                    className="!inset-y-4 !right-4 !h-auto !p-0 !w-[85vw] !max-w-4xl rounded-2xl border shadow-2xl bg-background overflow-hidden flex gap-0"
                >
                    {/* Left Sidebar - Progress */}
                    <div className="w-1/3 bg-muted/30 border-r border-border/50 p-6 hidden md:flex flex-col relative overflow-hidden">
                        {/* Decorative background elements */}
                        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/5 to-transparent pointer-events-none" />
                        <div className="absolute bottom-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl translate-x-1/2 translate-y-1/2 pointer-events-none" />

                        <div className="mb-8 relative z-10">
                            <h2 className="text-2xl font-semibold tracking-tight">
                                {isEditMode ? 'Edit Maintenance' : 'Add Maintenance'}
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                {isEditMode ? 'Update the maintenance record details.' : 'Log a new maintenance record for this vehicle.'}
                            </p>
                        </div>

                        <div className="space-y-1 relative z-10 flex-1">
                            {STEPS.map((s, i) => {
                                const Icon = s.icon;
                                const isActive = i === step;
                                const isCompleted = i < step;
                                return (
                                    <div
                                        key={s.key}
                                        className={cn(
                                            "group flex items-center gap-3 p-3 rounded-xl transition-all duration-300",
                                            isActive ? "bg-background shadow-sm ring-1 ring-border" : "hover:bg-background/50",
                                            isCompleted ? "opacity-70" : "opacity-50",
                                            isActive && "!opacity-100"
                                        )}
                                    >
                                        <div className={cn(
                                            "h-10 w-10 rounded-full flex items-center justify-center transition-colors duration-300",
                                            isActive ? "bg-primary text-primary-foreground" :
                                                isCompleted ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground",
                                        )}>
                                            {isCompleted ? <Check className="h-5 w-5" /> : <Icon className="h-5 w-5" />}
                                        </div>
                                        <div>
                                            <p className={cn(
                                                "font-medium text-sm transition-colors",
                                                isActive ? "text-foreground" : "text-muted-foreground"
                                            )}>
                                                {s.label}
                                            </p>
                                            <p className="text-xs text-muted-foreground/70 hidden lg:block">
                                                {s.description}
                                            </p>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="mt-auto flex items-center gap-4">
                        <Button
                            variant="ghost"
                            size="sm"
                            className="w-fit text-muted-foreground hover:text-foreground p-0 h-auto hover:bg-transparent"
                            onClick={handleClose}
                        >
                            <X className="h-4 w-4 mr-2" />
                            Close
                        </Button>

                        {isEditMode && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="w-fit text-destructive hover:text-destructive p-0 h-auto hover:bg-transparent"
                                onClick={() => setShowDeleteConfirm(true)}
                                disabled={deleteMutation.isPending}
                            >
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                            </Button>
                        )}
                        </div>
                    </div>

                    {/* Right Content */}
                    <div className="flex-1 flex flex-col h-full relative">
                        {/* Mobile Header */}
                        <div className="md:hidden p-4 border-b flex items-center justify-between">
                            <div>
                                <h2 className="font-semibold">Step {step + 1} of {STEPS.length}</h2>
                                <p className="text-xs text-muted-foreground">{STEPS[step].label}</p>
                            </div>
                            <Button variant="ghost" size="icon" onClick={handleClose}>
                                <X className="h-5 w-5" />
                            </Button>
                        </div>

                        {/* Main Form Area */}
                        <div className="flex-1 overflow-y-auto overflow-x-hidden p-6 md:p-10">
                            <AnimatePresence mode="wait" initial={false} custom={direction}>
                                <motion.div
                                    key={step}
                                    custom={direction}
                                    variants={variants}
                                    initial="enter"
                                    animate="center"
                                    exit="exit"
                                    transition={{
                                        x: { type: "spring", stiffness: 300, damping: 30 },
                                        opacity: { duration: 0.2 }
                                    }}
                                    className="h-full max-w-2xl mx-auto"
                                >
                                    <div className="mb-6">
                                        <h3 className="text-2xl font-bold tracking-tight">{STEPS[step].label}</h3>
                                        <p className="text-muted-foreground">{STEPS[step].description}</p>
                                    </div>
                                    {renderStep()}
                                </motion.div>
                            </AnimatePresence>
                        </div>

                        {/* Sticky Footer */}
                        <div className="p-6 border-t bg-background/80 backdrop-blur-md sticky bottom-0 z-20">
                            <div className="max-w-2xl mx-auto flex items-center justify-between">
                                <Button
                                    variant="outline"
                                    onClick={handleBack}
                                    disabled={step === 0 || mutation.isPending}
                                    className="w-24 rounded-full"
                                >
                                    <ChevronLeft className="h-4 w-4 mr-1" />
                                    Back
                                </Button>
                                {step < STEPS.length - 1 ? (
                                    <Button
                                        onClick={handleNext}
                                        disabled={!canGoNext()}
                                        className="w-32 rounded-full shadow-lg hover:shadow-primary/25 transition-all"
                                    >
                                        Next
                                        <ChevronRight className="h-4 w-4 ml-1" />
                                    </Button>
                                ) : (
                                    <Button
                                        onClick={() => mutation.mutate()}
                                        disabled={mutation.isPending}
                                        className="w-40 rounded-full shadow-lg hover:shadow-emerald-500/25 transition-all bg-emerald-600 hover:bg-emerald-700"
                                    >
                                        {mutation.isPending ? (
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        ) : (
                                            <Check className="h-4 w-4 mr-2" />
                                        )}
                                        Complete
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </SheetContent>
            </Sheet>

            <AlertDialog open={showCloseConfirm} onOpenChange={setShowCloseConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Discard changes?</AlertDialogTitle>
                        <AlertDialogDescription>
                            You have unsaved changes. Are you sure you want to discard them?
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Continue Editing</AlertDialogCancel>
                        <AlertDialogAction onClick={resetAndClose} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Discard
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete maintenance?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete this maintenance record. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => deleteMutation.mutate()}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </>
    );
}
